// Framework-agnostic info-modal config builder. Mirrors `widget-config.builder.ts`: it lives in
// shared constants so both the valet frontend (info-modal editor) and backend services
// (merchant-svc store onboarding) compile the exact same default info-modal JSON from a single
// source of truth. MUST NOT import any Nuxt/Pinia/browser APIs — it is imported by NestJS services.
import { InfoModalFormValues, WidgetType } from './widget-config.types';

/**
 * The default info-modal form values a fresh modal starts from. `customizations` overrides
 * individual fields; unknown and null-ish keys are ignored so a partial stored blob can be
 * rehydrated without punching holes in the defaults.
 */
export function createInitialInfoModalFormValues(
  customizations?: Partial<InfoModalFormValues>,
): InfoModalFormValues {
  const defaults: InfoModalFormValues = {
    infoModalName: '',
    infoModalType: WidgetType.INFO_MODAL,
    modalChecklistItems: [
      { enabled: true, id: '1', text: 'Theft' },
      { enabled: true, id: '2', text: 'Damaged items' },
      { enabled: true, id: '3', text: 'Missing items' },
      { enabled: true, id: '4', text: 'Lost packages' },
      { enabled: true, id: '5', text: 'Wrong items' },
      { enabled: true, id: '6', text: 'Returned to sender' },
    ],
    modalFileClaimLink: 'https://claim.orderprotection.com',
    modalFooterText:
      '* Contingent upon claim approval. In some cases, additional time may be required for investigation.',
    modalHeading: 'Get shipping protection & 24/7 support.',
    modalLogoHeight: 20,
    modalLogoImage: 'https://storage.googleapis.com/cdn.orderprotection.com/Images/cart-info-modal/OPLogoPurple2.svg',
    modalShowCopyright: true,
    modalStep1Image:
      'https://storage.googleapis.com/cdn.orderprotection.com/Images/cart-info-modal/ShieldCheckPurple2.svg',
    modalStep1Title: 'Add shipping protection to your order.',
    modalStep2Image:
      'https://storage.googleapis.com/cdn.orderprotection.com/Images/cart-info-modal/StatusWarningPurple2.svg',
    modalStep2Title: 'Have a shipping issue? Let us know.',
    modalStep3Image:
      'https://storage.googleapis.com/cdn.orderprotection.com/Images/cart-info-modal/PackageArrivedPurple2.svg',
    modalStep3Title: 'Get a resolution in as little as one hour. *',
    modalSubheading: 'Shipping Issues are over',
    powerUserConfig: '',
    showInfoModal: true,
  };

  const result = { ...defaults };

  if (customizations && typeof customizations === 'object') {
    (Object.keys(customizations) as Array<keyof InfoModalFormValues>).forEach(key => {
      if (key in result && customizations[key] != null) {
        (result as Record<string, unknown>)[key] = customizations[key];
      }
    });
  }

  return result;
}

/** Next free `Info Modal #NN` name for a store, given the modals it already has. */
export function generateInfoModalName(existingModals: Array<{ type?: string; name?: string }> = []): string {
  const count = existingModals.length + 1;

  const baseName = `Info Modal #${count.toString().padStart(2, '0')}`;

  let finalName = baseName;
  let counter = count;
  while (existingModals.some(modal => modal.name === finalName)) {
    counter++;
    finalName = `Info Modal #${counter.toString().padStart(2, '0')}`;
  }

  return finalName;
}

/**
 * Compiles the stored config JSON from the form values. The info modal renders from a flat blob
 * rather than an element tree, so this is a whitelist copy: editor-only fields (`infoModalName`,
 * `infoModalType`, `powerUserConfig`) deliberately stay out of the published config.
 */
export function compileInfoModalConfig(formValues: InfoModalFormValues): Record<string, unknown> {
  return {
    modalChecklistItems: formValues.modalChecklistItems,
    modalFileClaimLink: formValues.modalFileClaimLink,
    modalFooterText: formValues.modalFooterText,
    modalHeading: formValues.modalHeading,
    modalLogoHeight: formValues.modalLogoHeight,
    modalLogoImage: formValues.modalLogoImage,
    modalShowCopyright: formValues.modalShowCopyright,
    modalStep1Image: formValues.modalStep1Image,
    modalStep1Title: formValues.modalStep1Title,
    modalStep2Image: formValues.modalStep2Image,
    modalStep2Title: formValues.modalStep2Title,
    modalStep3Image: formValues.modalStep3Image,
    modalStep3Title: formValues.modalStep3Title,
    modalSubheading: formValues.modalSubheading,
    showInfoModal: formValues.showInfoModal,
  };
}

/** Backfills every field on a stored blob so the editor never binds a control to `undefined`. */
export function normalizeInfoModalFormValues(values: InfoModalFormValues): InfoModalFormValues {
  if (!values) return values;

  return {
    infoModalName: values.infoModalName ?? '',
    infoModalType: values.infoModalType ?? 'INFO_MODAL',
    modalChecklistItems: values.modalChecklistItems ?? [],
    modalFileClaimLink: values.modalFileClaimLink ?? '',
    modalFooterText: values.modalFooterText ?? '',
    modalHeading: values.modalHeading ?? '',
    modalLogoHeight: values.modalLogoHeight ?? 0,
    modalLogoImage: values.modalLogoImage ?? '',
    modalShowCopyright: values.modalShowCopyright ?? true,
    modalStep1Image: values.modalStep1Image ?? '',
    modalStep1Title: values.modalStep1Title ?? '',
    modalStep2Image: values.modalStep2Image ?? '',
    modalStep2Title: values.modalStep2Title ?? '',
    modalStep3Image: values.modalStep3Image ?? '',
    modalStep3Title: values.modalStep3Title ?? '',
    modalSubheading: values.modalSubheading ?? '',
    powerUserConfig: values.powerUserConfig ?? '',
    showInfoModal: values.showInfoModal ?? false,
  };
}

/**
 * Convenience builder for onboarding/server-side seeding: produces a publish-ready default
 * info-modal config (compiled JSON) plus the matching customization values. The info-modal
 * counterpart to `buildDefaultWidgetConfig`, used by merchant-svc to seed a fresh store.
 */
export function buildDefaultInfoModalConfig(name: string = generateInfoModalName()): {
  config: Record<string, unknown>;
  customizationValues: InfoModalFormValues;
} {
  const customizationValues = createInitialInfoModalFormValues({ infoModalName: name });
  const config = compileInfoModalConfig(customizationValues);

  return { config, customizationValues };
}
